import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Card, CardContent, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import cotizacionVentaService, { CotizacionVenta, CotizacionVentaDetalle } from '../../services/cotizacionVentaService';
import clienteService from '../../services/clienteService';
import productoService from '../../services/productoService';
import movMonedaService from '../../services/movMonedaService';
import ubicacionService from '../../services/ubicacionService';
import { IUbicacion } from '../../interfaces/Ubicacion';
import CotizacionForm from './components/CotizacionForm';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import ProductoSearch from '../pedidoventa/components/ProductoSearch';
import PDFViewer from '../../components/shared/PDFViewer';

type CotizacionVentaUI = CotizacionVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type CotizacionVentaUIExt = CotizacionVentaUI & { nroSuc?: number; cuit?: string; tipoIVA?: string; dni?: string; idCompania?: string };

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
const DEFAULT_NRO_SUC = 1;

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') return '';
  return strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  const match = FORMAS_DE_PAGO_VALIDAS.find(forma => forma.toUpperCase() === cleaned);
  return match || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as any)?.condvta ?? null);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) return 0;
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.max(0, Math.min(100, numero)) : 0;
};

const CotizacionesVenta: React.FC = () => {
  const navigate = useNavigate();
  const { cotizacionId } = useParams<{ cotizacionId?: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadingCotizacion, setLoadingCotizacion] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modo = cotizacionId ? (location.pathname.includes('/ver') ? 'ver' : 'editar') : 'crear';
  const [cotizacionVenta, setCotizacionVenta] = useState<CotizacionVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '10',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 'ARS',
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: DEFAULT_NRO_SUC,
    cuit: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  const isEditMode = modo === 'editar';
  const isReadOnlyByPath = modo === 'ver';
  const isReadOnlyByEstado = (cotizacionVenta.estado === 'Facturado' || cotizacionVenta.estado === 'Preparado') && isEditMode;
  const isReadOnly = isReadOnlyByPath || isReadOnlyByEstado;

  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [productoDialogOpen, setProductoDialogOpen] = useState(false);
  const [productos, setProductos] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroCotizacionCreada, setNroCotizacionCreada] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [cotizacionUSD, setCotizacionUSD] = useState<number | null>(null);
  const round2 = (n: number) => Math.round(n * 100) / 100;

  useEffect(() => {
    (async () => {
      try {
        const v = await movMonedaService.getUltimaCotizacion();
        setCotizacionUSD(v && Number.isFinite(v) && v > 0 ? v : null);
      } catch {
        setCotizacionUSD(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (cotizacionId) {
      const cargarCotizacion = async () => {
        try {
          setLoadingCotizacion(true);
          setError(null);
          const id = parseInt(cotizacionId, 10);
          if (isNaN(id)) {
            setError('ID de cotización inválido');
            navigate('/ventas/cotizaciones');
            return;
          }

          const cotizacionCargada = await cotizacionVentaService.getCotizacionVenta(id);
          let ubicacionFormateada = (cotizacionCargada as any).ubicacion || '';
          const nroUbicacionOrigen = (cotizacionCargada as any).nroUbicacionOrigen || (cotizacionCargada as any).nroUbicacion;

          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const ubicacionEncontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = ubicacionEncontrada
                ? `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = (cotizacionCargada as any).transporte || '';
          const nroExpreso = (cotizacionCargada as any).nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const detallesConSubtotales = cotizacionCargada.detalles?.map((detalle) => {
            const precioLista = (detalle as any).precioLista || detalle.precioUnitario || 0;
            const porcDesc = (detalle as any).porcDesc || 0;
            const subtotal = detalle.subtotal || 0;
            return { ...detalle, precioUnitario: precioLista, precioLista, descuento: porcDesc, porcDesc, subtotal };
          }) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((sum, d) => sum + (d.subtotal || 0), 0);
          const fechaEmisionDate = cotizacionCargada.fechaEmision ? (cotizacionCargada.fechaEmision instanceof Date ? cotizacionCargada.fechaEmision : new Date(cotizacionCargada.fechaEmision)) : new Date();
          const horaEmisionDate = cotizacionCargada.horaEmision ? (cotizacionCargada.horaEmision instanceof Date ? cotizacionCargada.horaEmision : new Date(cotizacionCargada.horaEmision)) : new Date();

          setCotizacionVenta({
            ...cotizacionCargada,
            fechaEmision: fechaEmisionDate,
            horaEmision: horaEmisionDate,
            ordenCompra: (cotizacionCargada as any).ordenDeCompra || (cotizacionCargada as any).ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (cotizacionCargada as any).formaDePago || '',
            nroSuc: (cotizacionCargada as any).nroSuc || DEFAULT_NRO_SUC,
            cuit: (cotizacionCargada as any).cuit || '',
            tipoIVA: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            dni: (cotizacionCargada as any).dni || '',
            idCompania: (cotizacionCargada as any).idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: cotizacionCargada.importeTotal && cotizacionCargada.importeTotal > 0 ? cotizacionCargada.importeTotal : round2(importeTotalCalculado),
          } as CotizacionVentaUIExt);
        } catch (err: any) {
          const message = err?.response?.data || 'Error al cargar la cotización';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setTimeout(() => navigate('/ventas/cotizaciones'), 2000);
        } finally {
          setLoadingCotizacion(false);
        }
      };
      cargarCotizacion();
    }
  }, [cotizacionId, navigate, enqueueSnackbar]);

  const handleProductoSearch = async () => {
    try {
      setLoading(true);
      const response = await productoService.getProductos({ searchTerm, pageNumber: 1, pageSize: 50 });
      setProductos(response.items);
    } catch (err: any) {
      enqueueSnackbar('Error al buscar productos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClienteSelect = (cliente: any) => {
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    setCotizacionVenta((prev: CotizacionVentaUIExt) => ({
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      formaDePago: formaPagoSugerida,
      transporte: transporteSugerido,
      porcDesc: normalizarPorcentajeBonificacion(cliente.porcDesc ?? (cliente as any)?.porc_Desc ?? (cliente as any)?.porcdesc),
      porcDesc2: normalizarPorcentajeBonificacion(cliente.porcDesc2 ?? (cliente as any)?.porc_Desc2 ?? (cliente as any)?.porcdesc2),
      porcDesc3: normalizarPorcentajeBonificacion(cliente.porcDesc3 ?? (cliente as any)?.porc_Desc3 ?? (cliente as any)?.porcdesc3),
    }));
    setClienteDialogOpen(false);
  };

  const handleProductoSelect = (producto: any) => {
    if (!cotizacionVenta.nroCliente || cotizacionVenta.nroCliente <= 0) {
      enqueueSnackbar('Debe seleccionar un cliente antes de agregar productos', { variant: 'warning' });
      return;
    }
    const base = Number(producto.precioVenta ?? producto.precio ?? producto.costo ?? producto.precioLista ?? producto.precioUnitario ?? 0);
    const tipoMoneda = producto.tipoMoneda ?? producto.tipoMonedaArticulo ?? 0;
    const baseRed = round2(base);
    const precio = (Number(tipoMoneda) === 1 && cotizacionUSD && cotizacionUSD > 0) ? round2(baseRed * cotizacionUSD) : baseRed;
    const nuevoDetalle: CotizacionVentaDetalle = {
      nroIdformulario: cotizacionVenta.nroFormulario,
      nroItem: cotizacionVenta.detalles.length + 1,
      codigoArticulo: producto.codigoArticulo,
      descripcionArticulo: producto.descripcion,
      cantidad: 1,
      precioUnitario: precio,
      precioLista: precio,
      descuento: 0,
      porcDesc: 0,
      subtotal: precio,
    };
    setCotizacionVenta((prev: CotizacionVentaUIExt) => ({
      ...prev,
      detalles: [...prev.detalles, nuevoDetalle],
      importeTotal: (prev.importeTotal || 0) + precio,
    }));
    setProductoDialogOpen(false);
  };

  useEffect(() => {
    const v = Number(cotizacionVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [cotizacionVenta.nroSuc]);

  useEffect(() => {
    const value = cotizacionVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            setCotizacionVenta(prev => {
              const cuitPreservar = (isEditMode || isReadOnly) && prev.cuit ? prev.cuit : (c.cuit || '');
              const tipoIVAPreservar = (isEditMode || isReadOnly) && prev.tipoIVA ? prev.tipoIVA : (c.tipoIva || '');
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: cuitPreservar,
                dni: (c as any).dni || '',
                tipoIVA: tipoIVAPreservar,
                formaDePago: (isEditMode || isReadOnly) && (prev as CotizacionVentaUI).formaDePago ? (prev as CotizacionVentaUI).formaDePago : formaPagoSugerida,
                transporte: (isEditMode || isReadOnly) && prev.transporte ? prev.transporte : transporteSugerido,
                porcDesc: (isEditMode || isReadOnly) && (prev.porcDesc !== undefined && prev.porcDesc !== null) ? prev.porcDesc : normalizarPorcentajeBonificacion(c.porcDesc ?? (c as any)?.porc_Desc ?? (c as any)?.porcdesc),
                porcDesc2: (isEditMode || isReadOnly) && (prev.porcDesc2 !== undefined && prev.porcDesc2 !== null) ? prev.porcDesc2 : normalizarPorcentajeBonificacion(c.porcDesc2 ?? (c as any)?.porc_Desc2 ?? (c as any)?.porcdesc2),
                porcDesc3: (isEditMode || isReadOnly) && (prev.porcDesc3 !== undefined && prev.porcDesc3 !== null) ? prev.porcDesc3 : normalizarPorcentajeBonificacion(c.porcDesc3 ?? (c as any)?.porc_Desc3 ?? (c as any)?.porcdesc3),
              } as CotizacionVentaUIExt;
            });
          } catch {}
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [cotizacionVenta.nroCliente, isEditMode, isReadOnly]);

  const handleSave = async () => {
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar una cotización en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];
      if (!cotizacionVenta.nroCliente || cotizacionVenta.nroCliente <= 0) erroresValidacion.push('Debe seleccionar un cliente');
      if (!cotizacionVenta.fechaEmision) erroresValidacion.push('Debe ingresar la Fecha de Emisión');
      const ubicacion = (cotizacionVenta as CotizacionVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') erroresValidacion.push('Debe seleccionar una Ubicación');
      const formaDePago = (cotizacionVenta as CotizacionVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') erroresValidacion.push('Debe seleccionar una Forma de Pago');
      if (!cotizacionVenta.detalles || cotizacionVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto a la cotización');
      } else {
        cotizacionVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene código de artículo`);
          }
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((e) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 5000 }));
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const payload = {
        ...cotizacionVenta,
        nroSuc: cotizacionVenta.nroSuc || DEFAULT_NRO_SUC,
        ordenCompra: cotizacionVenta.ordenCompra || '',
        transporte: cotizacionVenta.transporte || '',
        ubicacion: cotizacionVenta.ubicacion || '',
        formaDePago: cotizacionVenta.formaDePago || '',
      } as any;

      let response: CotizacionVenta;
      let nroCotizacion: number | null = null;

      if (isEditMode && cotizacionId) {
        const id = parseInt(cotizacionId, 10);
        await cotizacionVentaService.updateCotizacionVenta(id, payload);
        nroCotizacion = id;
        enqueueSnackbar('Cotización actualizada exitosamente', { variant: 'success', autoHideDuration: 3000 });
        setTimeout(() => navigate('/ventas/cotizaciones'), 1500);
        setLoading(false);
        return;
      } else {
        response = await cotizacionVentaService.createCotizacionVenta(payload);
        if (response?.nroFormulario) nroCotizacion = response.nroFormulario;
        else if ((response as any)?.formulario?.nroFormulario) nroCotizacion = (response as any).formulario.nroFormulario;
      }

      if (nroCotizacion && !isEditMode) {
        setNroCotizacionCreada(nroCotizacion);
        setSuccessDialogOpen(true);
        try {
          setLoadingPDF(true);
          const blob = await cotizacionVentaService.getCotizacionVentaPDF(nroCotizacion);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Cotizacion_${nroCotizacion}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setPdfUrl(url);
          setTimeout(() => setSuccessDialogOpen(false), 1500);
          enqueueSnackbar(`PDF de la cotización #${nroCotizacion} descargado correctamente`, { variant: 'success', autoHideDuration: 3000 });
        } catch (pdfError) {
          enqueueSnackbar('Error al descargar el PDF de la cotización', { variant: 'warning' });
        } finally {
          setLoadingPDF(false);
        }
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: DEFAULT_NRO_SUC,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
      } else {
        enqueueSnackbar('Cotización guardada exitosamente', { variant: 'success' });
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: DEFAULT_NRO_SUC,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let errorMessage = 'Error al guardar la cotización';
      if (err?.message && (err.message.includes('Se encontraron') || err.message.includes('obligatorio'))) {
        errorMessage = err.message;
        const partes = err.message.split('\n');
        const errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        errores.forEach((e: string) => { if (e.trim()) enqueueSnackbar(e.trim(), { variant: 'error', autoHideDuration: 6000 }); });
      } else if (data?.errors && typeof data.errors === 'object') {
        const msgs: string[] = [];
        for (const k of Object.keys(data.errors)) {
          const val = data.errors[k];
          if (Array.isArray(val)) msgs.push(`${k}: ${val.join(' - ')}`);
          else if (val) msgs.push(`${k}: ${String(val)}`);
        }
        if (msgs.length > 0) {
          errorMessage = msgs.join('\n');
          msgs.forEach((m: string) => enqueueSnackbar(m, { variant: 'error', autoHideDuration: 6000 }));
        }
      } else if (status === 400 || status === 500) {
        errorMessage = data?.message || errorMessage;
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => setSuccessDialogOpen(false);

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setNroCotizacionCreada(null);
    navigate('/ventas/cotizaciones');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isReadOnly
                ? `Ver Cotización de Venta #${cotizacionId}`
                : isEditMode
                  ? `Editar Cotización de Venta #${cotizacionId}`
                  : 'Nueva Cotización de Venta'}
            </Typography>
            {(isReadOnly || isEditMode) && (
              <Button
                variant="outlined"
                onClick={() => navigate('/ventas/cotizaciones')}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Volver
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
          )}

          {loadingCotizacion ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><Typography>Cargando cotización...</Typography></Box>
          ) : (
            <CotizacionForm
              cotizacion={cotizacionVenta}
              onCotizacionChange={setCotizacionVenta}
              onClienteSearch={() => setClienteDialogOpen(true)}
              onProductoSearch={() => setProductoDialogOpen(true)}
              onSave={handleSave}
              loading={loading}
              readOnly={isReadOnly}
              saveButtonText={isEditMode ? 'Actualizar Cotización' : 'Guardar Cotización'}
            />
          )}
        </CardContent>
      </Card>

      <ClienteSearch open={clienteDialogOpen} onClose={() => setClienteDialogOpen(false)} onSelect={handleClienteSelect} />

      <ProductoSearch
        open={productoDialogOpen}
        onClose={() => setProductoDialogOpen(false)}
        onSearch={handleProductoSearch}
        onSelect={handleProductoSelect}
        productos={productos}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        loading={loading}
      />

      <Dialog open={successDialogOpen} onClose={handleCloseSuccessDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2, boxShadow: 6 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', pb: 1.5, pt: 3, px: 3 }}>Cotización Creada</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Cotización creada exitosamente! Número: {nroCotizacionCreada}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseSuccessDialog} variant="text" color="primary" sx={{ minWidth: 80, textTransform: 'none', fontWeight: 500 }} disabled={loadingPDF}>
            {loadingPDF ? 'Generando PDF...' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      {pdfUrl && nroCotizacionCreada && (
        <PDFViewer pdfUrl={pdfUrl} onClose={handleClosePDF} title={`Cotización #${nroCotizacionCreada}`} documentId={`${nroCotizacionCreada}`} documentType="cotizacion" />
      )}
    </Box>
  );
};

export default CotizacionesVenta;
